<!--
  活动总裁驾驶舱 - DataV 风格（对齐商城）
  - 菜单内嵌：/activity/cockpit
  - 新窗口纯净全屏：/activity/cockpit-view
-->
<template>
  <div class="activity-cockpit" :class="{ 'is-pure': isPureView }" v-loading="loading">
    <Decoration8 class="cockpit-top-line" :color="['#1c9cff', '#0f6cad']" />

    <header class="cockpit-header">
      <Decoration3 class="cockpit-header-deco" :color="['#1c9cff', '#0f6cad']" />
      <div class="cockpit-header-main">
        <h1>活动总裁驾驶舱</h1>
        <p>活动运营指标实时监控 · {{ nowText }}</p>
      </div>
      <Decoration3 class="cockpit-header-deco is-rev" :color="['#1c9cff', '#0f6cad']" />
      <div class="cockpit-header-actions">
        <el-button class="cockpit-refresh" type="primary" plain :loading="loading" @click="reload">
          刷新
        </el-button>
      </div>
    </header>

    <div class="kpi-row">
      <BorderBox12 v-for="item in kpiItems" :key="item.label" class="kpi-panel">
        <div class="kpi-inner">
          <div class="kpi-label">{{ item.label }}</div>
          <DigitalFlop :config="item.flop" class="kpi-flop" />
          <div class="kpi-foot">
            <span v-if="item.rateText" :class="item.up ? 'up' : 'down'">{{ item.rateText }}</span>
            <em>{{ item.note }}<template v-if="item.refText"> {{ item.refText }}</template></em>
          </div>
        </div>
      </BorderBox12>
    </div>

    <div class="cockpit-grid cockpit-grid-2">
      <BorderBox12 class="panel">
        <div class="panel-title">运营待办</div>
        <div class="todo-grid">
          <div
            v-for="item in todoItems"
            :key="item.key"
            class="todo-tile"
            :class="{ 'is-clickable': !isPureView && item.count > 0 }"
            @click="openTodo(item)"
          >
            <DigitalFlop :config="item.flop" class="todo-flop" />
            <div class="todo-label">{{ item.label }}</div>
            <div class="todo-desc">{{ item.desc }}</div>
          </div>
        </div>
      </BorderBox12>
      <BorderBox12 class="panel">
        <div class="panel-title">活动状态分布</div>
        <Echart :height="280" :options="statusChartOptions" />
      </BorderBox12>
    </div>

    <div class="cockpit-grid cockpit-grid-2">
      <BorderBox12 class="panel">
        <div class="panel-title">报名漏斗</div>
        <Echart :height="280" :options="funnelChartOptions" />
      </BorderBox12>
      <BorderBox12 class="panel">
        <div class="panel-title">近 7 日新增报名</div>
        <Echart :height="280" :options="trendChartOptions" />
      </BorderBox12>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BorderBox12, Decoration3, Decoration8, DigitalFlop } from '@kjgl77/datav-vue3'
import type { EChartsOption } from 'echarts'
import * as ActivityApi from '@/api/activity/activity'
import * as ActivitySignupApi from '@/api/activity/signup'
import { formatDate } from '@/utils/formatTime'
import { calculateRelativeRate } from '@/utils'
import {
  ACTIVITY_CEO_COCKPIT_VIEW_NAME,
  ACTIVITY_CEO_COCKPIT_VIEW_PATH,
  resolveActivityBasePath
} from '@/layout/standalone/activityMenuScopes'

defineOptions({ name: 'ActivityCockpit' })

const route = useRoute()
const router = useRouter()

const isPureView = computed(
  () =>
    route.name === ACTIVITY_CEO_COCKPIT_VIEW_NAME || route.path === ACTIVITY_CEO_COCKPIT_VIEW_PATH
)

const loading = ref(true)
const nowText = ref(formatDate(new Date(), 'YYYY-MM-DD HH:mm:ss'))

const metrics = reactive({
  activities: 0,
  signedUp: 0,
  pendingAudit: 0,
  pendingSignups: 0,
  todaySignups: 0,
  yesterdaySignups: 0
})

const activityStatus = reactive({
  published: 0,
  ongoing: 0,
  finished: 0,
  closed: 0
})

const funnel = reactive({
  waitingPay: 0,
  pendingSignups: 0,
  signedUp: 0,
  checkedIn: 0
})

const trendDays = ref<Array<{ day: string; weekday: string; count: number }>>([])

let clockTimer: ReturnType<typeof setInterval> | undefined

const pageParams = { pageNo: 1, pageSize: 1 }
const WEEKDAY_LABELS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
const pad2 = (n: number) => String(n).padStart(2, '0')
const formatLocalDateTime = (date: Date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`

const buildDayRange = (offsetFromToday: number) => {
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  start.setDate(start.getDate() - offsetFromToday)
  const end = new Date(start)
  end.setHours(23, 59, 59, 0)
  return [formatLocalDateTime(start), formatLocalDateTime(end)] as [string, string]
}

const buildLast7Days = () =>
  Array.from({ length: 7 }, (_, i) => {
    const start = new Date()
    start.setHours(0, 0, 0, 0)
    start.setDate(start.getDate() - (6 - i))
    const end = new Date(start)
    end.setHours(23, 59, 59, 0)
    return {
      day: `${pad2(start.getMonth() + 1)}-${pad2(start.getDate())}`,
      weekday: WEEKDAY_LABELS[start.getDay()],
      addTime: [formatLocalDateTime(start), formatLocalDateTime(end)] as [string, string]
    }
  })

const formatRate = (value: number, reference: number) => {
  const rate = Number(calculateRelativeRate(value, reference) || 0)
  const up = rate >= 0
  return {
    up,
    rateText: `${up ? '+' : ''}${Math.abs(rate).toFixed(1)}%`
  }
}

const signInRate = computed(() => {
  if (!metrics.signedUp) return 0
  return Math.round((funnel.checkedIn / metrics.signedUp) * 1000) / 10
})

const activityBase = computed(() => resolveActivityBasePath(route.path))

const getTotal = (res: any) => {
  if (typeof res === 'number') return res
  const data = res?.data ?? res
  if (typeof data === 'number') return data
  if (typeof data === 'string' && data !== '' && !Number.isNaN(Number(data))) return Number(data)
  if (typeof data?.total === 'number') return data.total
  if (Array.isArray(data?.list)) return data.list.length
  if (Array.isArray(data)) return data.length
  return 0
}

const safeTotal = async (requester: () => Promise<any>) => {
  try {
    return getTotal(await requester())
  } catch {
    return 0
  }
}

const buildFlop = (value: number) => ({
  number: [Number(value) || 0],
  content: '{nt}',
  toFixed: 0,
  style: {
    fontSize: 28,
    fill: '#00f2fe',
    fontWeight: 'bold'
  }
})

const todaySignupRate = computed(() =>
  formatRate(metrics.todaySignups, metrics.yesterdaySignups)
)

const kpiItems = computed(() => [
  { label: '活动总数', flop: buildFlop(metrics.activities), note: '全部活动', rateText: '', refText: '', up: true },
  {
    label: '有效报名',
    flop: buildFlop(metrics.signedUp),
    note: `签到率 ${signInRate.value}%`,
    rateText: '',
    refText: `已签到 ${funnel.checkedIn}`,
    up: true
  },
  {
    label: '今日新增报名',
    flop: buildFlop(metrics.todaySignups),
    note: '较昨日',
    rateText: todaySignupRate.value.rateText,
    refText: String(metrics.yesterdaySignups),
    up: todaySignupRate.value.up
  },
  { label: '待审核报名', flop: buildFlop(metrics.pendingSignups), note: '报名待审', rateText: '', refText: '', up: true }
])

type TodoItem = {
  key: string
  label: string
  desc: string
  count: number
  path: string
  query?: Record<string, string>
  flop: ReturnType<typeof buildFlop>
}

const todoItems = computed<TodoItem[]>(() => [
  {
    key: 'pending-audit',
    label: '待审核活动',
    desc: '用户发布待审',
    count: metrics.pendingAudit,
    path: '/activity',
    query: { status: '1' },
    flop: buildFlop(metrics.pendingAudit)
  },
  {
    key: 'pending-signup',
    label: '待审核报名',
    desc: '报名待运营审',
    count: metrics.pendingSignups,
    path: '/signup',
    query: { status: '2' },
    flop: buildFlop(metrics.pendingSignups)
  },
  {
    key: 'waiting-pay',
    label: '待支付报名',
    desc: '未完成支付',
    count: funnel.waitingPay,
    path: '/signup',
    query: { status: '4' },
    flop: buildFlop(funnel.waitingPay)
  }
])

const openTodo = (item: TodoItem) => {
  if (isPureView.value || item.count <= 0) return
  void router.push({
    path: `${activityBase.value}${item.path}`,
    query: { ...route.query, standalone: '1', app: 'activity', ...item.query }
  })
}

const statusChartOptions = computed<EChartsOption>(() => {
  const items = [
    { name: '已发布', value: activityStatus.published, color: '#10b981' },
    { name: '进行中', value: activityStatus.ongoing, color: '#3b82f6' },
    { name: '已结束', value: activityStatus.finished, color: '#64748b' },
    { name: '已关闭', value: activityStatus.closed, color: '#ef4444' }
  ].filter((item) => item.value > 0)
  if (!items.length) {
    items.push({ name: '暂无活动', value: 1, color: '#334155' })
  }
  return {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'item', formatter: '{b}：{c}（{d}%）' },
    legend: {
      bottom: 0,
      textStyle: { color: '#9fc4ee' }
    },
    series: [
      {
        type: 'pie',
        radius: ['42%', '68%'],
        center: ['50%', '44%'],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: '#071e3d', borderWidth: 2 },
        label: { color: '#e6f7ff', formatter: '{b}\n{c}' },
        data: items.map((item) => ({
          name: item.name,
          value: item.value,
          itemStyle: { color: item.color }
        }))
      }
    ]
  }
})

const funnelChartOptions = computed<EChartsOption>(() => {
  const items = [
    { label: '待支付', value: funnel.waitingPay, color: '#f59e0b' },
    { label: '待审核', value: funnel.pendingSignups, color: '#3b82f6' },
    { label: '已报名', value: funnel.signedUp, color: '#10b981' },
    { label: '已签到', value: funnel.checkedIn, color: '#06b6d4' }
  ]
  const max = Math.max(...items.map((item) => item.value), 1)
  return {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'item', formatter: '{b}：{c}' },
    series: [
      {
        type: 'funnel',
        left: '8%',
        top: 12,
        bottom: 12,
        width: '72%',
        min: 0,
        max,
        minSize: '12%',
        maxSize: '100%',
        sort: 'none',
        gap: 6,
        label: {
          show: true,
          position: 'inside',
          formatter: '{b}\n{c}',
          color: '#fff',
          fontSize: 12,
          lineHeight: 16
        },
        labelLine: { show: false },
        itemStyle: { borderColor: '#0a2748', borderWidth: 1 },
        data: items.map((item) => ({
          name: item.label,
          value: item.value,
          itemStyle: { color: item.color }
        }))
      }
    ]
  }
})

const trendChartOptions = computed<EChartsOption>(() => {
  const items = trendDays.value
  const values = items.map((item) => item.count)
  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const p = Array.isArray(params) ? params[0] : params
        const raw = items[p.dataIndex]
        return `${p.name}<br/>新增：${raw?.count ?? 0}`
      }
    },
    grid: { left: 12, right: 12, top: 28, bottom: 8, containLabel: true },
    xAxis: {
      type: 'category',
      data: items.map((item) => item.day),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: 'rgba(0,242,254,0.25)' } },
      axisLabel: {
        color: '#9fc4ee',
        formatter: (_value: string, index: number) => {
          const raw = items[index]
          return raw ? `${raw.day}\n${raw.weekday}` : ''
        }
      }
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      splitLine: { lineStyle: { color: 'rgba(159,196,238,0.12)', type: 'dashed' } },
      axisLabel: { color: '#9fc4ee' }
    },
    series: [
      {
        type: 'bar',
        barWidth: 28,
        data: values,
        itemStyle: {
          borderRadius: [6, 6, 2, 2],
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: '#00f2fe' },
              { offset: 1, color: '#1c9cff' }
            ]
          }
        },
        label: {
          show: true,
          position: 'top',
          color: '#e6f7ff',
          fontSize: 12
        }
      }
    ]
  }
})

const reload = async () => {
  loading.value = true
  try {
    const days = buildLast7Days()
    const todayRange = buildDayRange(0)
    const yesterdayRange = buildDayRange(1)
    const [
      activities,
      signedUp,
      pendingAudit,
      pendingSignups,
      waitingPay,
      checkedIn,
      todaySignups,
      yesterdaySignups,
      published,
      ongoing,
      finished,
      closed,
      ...dayTotals
    ] = await Promise.all([
      safeTotal(() => ActivityApi.getActivityPage(pageParams as any)),
      safeTotal(() => ActivitySignupApi.getSignupPage({ ...pageParams, status: 1 })),
      safeTotal(() => ActivityApi.getPendingAuditCount()),
      safeTotal(() => ActivitySignupApi.getSignupPage({ ...pageParams, status: 2 })),
      safeTotal(() => ActivitySignupApi.getSignupPage({ ...pageParams, status: 4 })),
      safeTotal(() => ActivitySignupApi.getSignupPage({ ...pageParams, status: 1, isSign: 1 })),
      safeTotal(() =>
        ActivitySignupApi.getSignupPage({ ...pageParams, addTime: todayRange })
      ),
      safeTotal(() =>
        ActivitySignupApi.getSignupPage({ ...pageParams, addTime: yesterdayRange })
      ),
      safeTotal(() => ActivityApi.getActivityPage({ ...pageParams, status: 3 })),
      safeTotal(() => ActivityApi.getActivityPage({ ...pageParams, status: 4 })),
      safeTotal(() => ActivityApi.getActivityPage({ ...pageParams, status: 5 })),
      safeTotal(() => ActivityApi.getActivityPage({ ...pageParams, status: 6 })),
      ...days.map((day) =>
        safeTotal(() =>
          ActivitySignupApi.getSignupPage({
            ...pageParams,
            addTime: day.addTime
          })
        )
      )
    ])
    metrics.activities = activities
    metrics.signedUp = signedUp
    metrics.pendingAudit = pendingAudit
    metrics.pendingSignups = pendingSignups
    metrics.todaySignups = todaySignups
    metrics.yesterdaySignups = yesterdaySignups
    funnel.waitingPay = waitingPay
    funnel.pendingSignups = pendingSignups
    funnel.signedUp = signedUp
    funnel.checkedIn = checkedIn
    activityStatus.published = published
    activityStatus.ongoing = ongoing
    activityStatus.finished = finished
    activityStatus.closed = closed
    trendDays.value = days.map((day, index) => ({
      day: day.day,
      weekday: day.weekday,
      count: Number(dayTotals[index]) || 0
    }))
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  nowText.value = formatDate(new Date(), 'YYYY-MM-DD HH:mm:ss')
  clockTimer = setInterval(() => {
    nowText.value = formatDate(new Date(), 'YYYY-MM-DD HH:mm:ss')
  }, 1000)
  await reload()
})

onBeforeUnmount(() => {
  if (clockTimer) clearInterval(clockTimer)
})
</script>

<style lang="scss" scoped>
.activity-cockpit {
  --cockpit-cyan: #00f2fe;
  --cockpit-blue: #1c9cff;
  --cockpit-text: #e6f7ff;
  --cockpit-muted: #9fc4ee;
  position: relative;
  min-height: 720px;
  padding: 10px 14px 18px;
  overflow: auto;
  color: var(--cockpit-text);
  background: radial-gradient(ellipse at 50% 0%, #0d3b66 0%, #071e3d 55%, #030c1e 100%);
  font-size: 13px;

  &.is-pure {
    position: fixed;
    inset: 0;
    z-index: 3000;
    min-height: 100vh;
    min-height: 100dvh;
  }
}

.cockpit-top-line {
  position: absolute;
  top: 4px;
  left: 50%;
  width: 58%;
  height: 18px;
  transform: translateX(-50%);
}

.cockpit-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 72px;
  margin-bottom: 10px;
  padding-top: 8px;
}

.cockpit-header-deco {
  width: 220px;
  height: 36px;
  margin: 0 8px;
  flex-shrink: 0;

  &.is-rev {
    transform: rotateY(180deg);
  }
}

.cockpit-header-main {
  text-align: center;

  h1 {
    margin: 0;
    font-size: 28px;
    letter-spacing: 6px;
    color: #fff;
    text-shadow: 0 0 12px rgba(0, 242, 254, 0.55);
  }

  p {
    margin: 6px 0 0;
    color: var(--cockpit-muted);
    font-size: 12px;
  }
}

.cockpit-header-actions {
  position: absolute;
  right: 0;
  top: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.cockpit-refresh {
  border-color: rgba(0, 242, 254, 0.45);
  color: var(--cockpit-cyan);
  background: rgba(0, 242, 254, 0.08);
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.kpi-panel {
  min-height: 128px;
}

.kpi-inner {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px 12px;
}

.kpi-label {
  color: var(--cockpit-muted);
  font-size: 13px;
}

.kpi-flop {
  height: 42px;
}

.kpi-foot {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;

  .up {
    color: #34d399;
    font-weight: 600;
  }

  .down {
    color: #f87171;
    font-weight: 600;
  }

  em {
    font-style: normal;
    color: #6f8fb3;
  }
}

.todo-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  padding: 12px 10px 16px;
  min-height: 280px;
  align-content: center;
}

.todo-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 220px;
  padding: 16px 10px;
  border: 1px solid rgba(0, 242, 254, 0.12);
  border-radius: 8px;
  background: rgba(7, 30, 61, 0.45);
  transition: border-color 0.2s, background 0.2s;

  &.is-clickable {
    cursor: pointer;

    &:hover {
      border-color: rgba(0, 242, 254, 0.45);
      background: rgba(28, 156, 255, 0.12);
    }
  }
}

.todo-flop {
  height: 38px;
}

.todo-label {
  color: var(--cockpit-text);
  font-size: 15px;
  font-weight: 600;
}

.todo-desc {
  color: var(--cockpit-muted);
  font-size: 12px;
  text-align: center;
}

.cockpit-grid {
  display: grid;
  gap: 10px;
  margin-bottom: 12px;
}

.cockpit-grid-2 {
  grid-template-columns: 1fr 1.2fr;
}

.panel {
  min-height: 320px;
  padding: 4px;
}

.panel-title {
  height: 34px;
  margin-bottom: 4px;
  padding: 0 10px;
  color: var(--cockpit-cyan);
  font-size: 15px;
  font-weight: 600;
  line-height: 34px;
  background: linear-gradient(90deg, rgba(28, 156, 255, 0.28), transparent);
}

@media (max-width: 1280px) {
  .kpi-row,
  .cockpit-grid-2 {
    grid-template-columns: 1fr 1fr;
  }

  .cockpit-header-deco {
    display: none;
  }

  .cockpit-header-actions {
    position: static;
    margin-top: 8px;
  }

  .cockpit-header {
    flex-direction: column;
  }
}

@media (max-width: 900px) {
  .kpi-row,
  .cockpit-grid-2,
  .todo-grid {
    grid-template-columns: 1fr;
  }
}
</style>
